"use client";

import { useCallback, useState, type ReactNode } from "react";
import { ArrowRight, ThumbsDown, ThumbsUp } from "phosphor-react";
import Modal from "../Modal/Modal.web";
import type {
  ExplainerCTA,
  ExplainerIntensity,
  ExplainerIntensityKey,
  ExplainerResponse,
  ExplainerType,
} from "@ishvaram/api-client";

export type ExplainableTrackEvent =
  | "explainable.opened"
  | "explainable.cta_clicked"
  | "explainable.dismissed";

export interface ExplainableProps {
  type: ExplainerType;
  id: string;
  intensity?: ExplainerIntensityKey;
  triggerLabel?: string;
  children?: ReactNode;
  data?: ExplainerResponse;
  loading?: boolean;
  error?: Error | null;
  onRequestData?: () => void;
  onTrack?: (event: ExplainableTrackEvent, props: Record<string, unknown>) => void;
  onCtaPress?: (cta: ExplainerCTA) => void;
  onFeedback?: (helpful: boolean) => void;
  surface?: string;
  className?: string;
}

export default function Explainable({
  type,
  id,
  intensity,
  triggerLabel,
  children,
  data,
  loading = false,
  error = null,
  onRequestData,
  onTrack,
  onCtaPress,
  onFeedback,
  surface,
  className = "",
}: ExplainableProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (!data) onRequestData?.();
    setOpen(true);
    onTrack?.("explainable.opened", { type, id, intensity, surface });
  }, [data, intensity, id, onRequestData, onTrack, surface, type]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onTrack?.("explainable.dismissed", { type, id, surface });
  }, [id, onTrack, surface, type]);

  const handleCtaPress = useCallback(() => {
    if (!data) return;
    onTrack?.("explainable.cta_clicked", { type, id, surface, product: data.cta.product });
    onCtaPress?.(data.cta);
  }, [data, id, onCtaPress, onTrack, surface, type]);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`inline cursor-pointer border-0 bg-transparent p-0 font-inherit text-text-primary underline decoration-dotted underline-offset-4 ${className}`}
        aria-haspopup="dialog"
        aria-label={triggerLabel ?? id}
      >
        {children ?? triggerLabel ?? id}
      </button>
      <Modal
        visible={open}
        onClose={handleClose}
        title={data?.display_name ?? "Loading…"}
        size="md"
      >
        <SheetBody
          data={data}
          loading={loading}
          error={error}
          intensity={intensity}
          onCtaPress={handleCtaPress}
          onFeedback={onFeedback}
        />
      </Modal>
    </>
  );
}

function SheetBody({
  data,
  loading,
  error,
  intensity,
  onCtaPress,
  onFeedback,
}: {
  data?: ExplainerResponse;
  loading: boolean;
  error: Error | null;
  intensity?: ExplainerIntensityKey;
  onCtaPress: () => void;
  onFeedback?: (helpful: boolean) => void;
}) {
  const [thumbState, setThumbState] = useState<"idle" | "up" | "down">("idle");
  const handleThumb = useCallback(
    (helpful: boolean) => {
      if (thumbState !== "idle") return;
      setThumbState(helpful ? "up" : "down");
      onFeedback?.(helpful);
    },
    [onFeedback, thumbState]
  );

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-body font-semibold text-text-primary">Could not load explanation</p>
        <p className="mt-2 text-body text-text-secondary">Please close and try again.</p>
      </div>
    );
  }
  if (loading || !data) {
    return <p className="p-6 text-center text-body text-text-secondary">Loading meaning…</p>;
  }

  const intensityBucket: ExplainerIntensity | null =
    data.intensity ??
    (intensity && data.intensity_buckets ? data.intensity_buckets[intensity] : null);

  return (
    <div className="max-h-[70vh] overflow-y-auto">
      {data.display_name_hi ? (
        <p className="text-body text-text-secondary">{data.display_name_hi}</p>
      ) : null}

      <SectionLabel>What it means</SectionLabel>
      <Body>{data.what_it_means}</Body>

      {intensityBucket ? (
        <>
          <SectionLabel>For your chart — {intensityBucket.label}</SectionLabel>
          <Body>{intensityBucket.prose}</Body>
        </>
      ) : null}

      <SectionLabel>Recommended upays</SectionLabel>
      {data.upays.map((upay) => (
        <div key={`${upay.title}-${upay.why}`} className="border-b border-border-subtle py-3">
          <p className="text-body font-semibold text-text-primary">{upay.title}</p>
          <p className="mt-2 text-body-small leading-relaxed text-text-secondary">{upay.why}</p>
          <p className="mt-1 text-body-small leading-relaxed text-text-secondary">{upay.how}</p>
        </div>
      ))}

      {onFeedback ? (
        <div className="mt-5 flex min-h-11 items-center justify-center gap-2.5">
          {thumbState === "idle" ? (
            <>
              <span className="text-body-small text-text-secondary">Was this helpful?</span>
              <FeedbackButton label="Yes, helpful" onClick={() => handleThumb(true)}>
                <ThumbsUp size={16} />
              </FeedbackButton>
              <FeedbackButton label="No, not helpful" onClick={() => handleThumb(false)}>
                <ThumbsDown size={16} />
              </FeedbackButton>
            </>
          ) : (
            <span className="text-body-small text-text-secondary">Thanks!</span>
          )}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onCtaPress}
        className="mt-6 flex min-h-11 w-full items-center justify-between rounded-xl bg-surface-brand-strong px-5 py-3 text-button font-semibold text-text-on-brand"
        aria-label={data.cta.label}
      >
        <span className="inline-flex items-center gap-2">
          {data.cta.label} <ArrowRight size={16} aria-hidden />
        </span>
        <span>₹{data.cta.price_inr}</span>
      </button>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 mt-4 text-eyebrow font-bold uppercase tracking-eyebrow text-text-secondary">
      {children}
    </p>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <p className="whitespace-pre-wrap text-body leading-relaxed text-text-primary">{children}</p>
  );
}

function FeedbackButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-border-default text-text-primary"
    >
      {children}
    </button>
  );
}
